import { useState, useRef } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import EmailChipsInput from './EmailChipsInput';
import type { EmailChipsInputHandle } from './EmailChipsInput';
import { LoadingButton } from '@mui/lab';
import { useMembershipApi } from '../api';
import { useData } from '../contexts/useData';
import { useAlert } from '@lendica/components';

export interface InviteDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export default function InviteDialog({ open, setOpen }: InviteDialogProps) {
    const membershipApi = useMembershipApi();
    const [loading, setLoading] = useState(false);
    const [emails, setEmails] = useState<string[]>([]);
    const emailInputRef = useRef<EmailChipsInputHandle>(null);
    const { company, refreshInvitations } = useData();
    const { setSuccessMessage, setErrorMessage } = useAlert();

    const handleClose = () => {
        setOpen(false);
    };

    const handleSendInvites = async () => {
        setLoading(true);
        if (!company?.id) return;
        // Flush any pending input and validate before submitting
        const ok = emailInputRef.current?.flushPending?.() ?? true;
        if (!ok) {
            setLoading(false);
            setErrorMessage('Please fix errors before sending invites.');
            return;
        }
        try {
            const response = await membershipApi.sendInvitations(emails);

            const result = response.data;

            const successful = result?.successful || [];
            const extended = result?.extended || [];
            const failed = result?.failed || [];

            const successParts: string[] = [];
            if (successful.length > 0) {
                successParts.push(
                    `Successfully sent ${successful.length} invite${
                        successful.length === 1 ? '' : 's'
                    }`
                );
            }
            if (extended.length > 0) {
                successParts.push(
                    `Extended ${extended.length} invite${extended.length === 1 ? '' : 's'}`
                );
            }
            if (successParts.length > 0) {
                setSuccessMessage(successParts.join('. ') + '.');
            }
            if (failed.length > 0) {
                const failedDetails = failed
                    .map(item => {
                        if (typeof item === 'string') {
                            return item;
                        }
                        // Show reason if available, otherwise just email
                        return item.reason ? `${item.email} (${item.reason})` : item.email;
                    })
                    .join(', ');
                setErrorMessage(`Failed to send invites to: ${failedDetails}`);
            }
            if (successful.length === 0 && extended.length === 0 && failed.length === 0) {
                setErrorMessage(
                    'No invites were sent. All emails may be invalid, duplicates, or already invited.'
                );
            }

            // Force-refresh invitations immediately so the table updates right away
            await refreshInvitations(true);

            setTimeout(() => {
                setLoading(false);
                setEmails([]);
                handleClose();
            }, 800);
        } catch (error: any) {
            setErrorMessage(error?.message || 'Failed to send invitations');
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                component: 'form',
                sx: {
                    paddingY: 1,
                    paddingX: 1,
                },
            }}
        >
            <DialogTitle>Invite team members</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Invite members to your company by adding their work emails below.
                </DialogContentText>
                <Stack sx={{ my: 2, gap: 1 }}>
                    <EmailChipsInput ref={emailInputRef} value={emails} setValue={setEmails} />
                    <TextField
                        label="Membership role"
                        fullWidth
                        value="Admin"
                        helperText="We only support admin role at the moment"
                        inputProps={{
                            readOnly: true,
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <LoadingButton
                    loading={loading}
                    onClick={handleSendInvites}
                    autoFocus
                    variant="contained"
                    color="primary"
                    disabled={emails.length === 0}
                >
                    Send invites
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
